import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PhotoCollectionDto } from '../../../../models/trofaco/photoCollectionDto';
import { PhotoDto } from '../../../../models/trofaco/photoDto';
import { ArrayHelper } from '../../../../models/helpers/array-helper';
import { ModalDirective } from 'ngx-bootstrap';
import { MapLocation } from '../../../core/components/map-picker/map-picker.component';
import { BaseComponent } from '../../../core/components/bases/base-component';
import { UserService } from '../../../core/services/user.service';
import { takeUntil } from 'rxjs/operators';
import { PhotoCollectionService } from '../../../core/services/photo-collection.service';
import { ConfirmDeleteModalComponent } from '../../../core/components/delete-modal/confirm-delete-modal-component';
import { PlantingSiteAreaDto } from '../../../../models/trofaco/plantingSiteAreaDto';
import { PlantingSiteDetailDto } from '../../../../models/trofaco/plantingSiteDetailDto';
import { ActivatedRoute } from '@angular/router';
import { CanDeactivateService } from 'src/app/modules/core/services/can-deactivate.service';
import { AppUserModel } from 'src/app/models/users/app-user.model';

@Component({
    selector: 'app-photos-list',
    templateUrl: './photos-list.component.html'
})
export class PhotosListComponent extends BaseComponent implements OnInit {
    @ViewChild('fullModal', {static: true}) fullscreenModal: ModalDirective;
    @ViewChild('wizardModal', {static: true}) wizardModal: ModalDirective;
    @ViewChild('editModal', {static: true}) editModal: ModalDirective;
    @ViewChild('fileInput', {static: true}) fileInput: ElementRef<HTMLInputElement>;
    @ViewChild(ConfirmDeleteModalComponent, {static:true}) confirmDelete: ConfirmDeleteModalComponent;

    @Input()
    collectionId: string;

    @Input()
    site: PlantingSiteDetailDto;

    @Input()
    siteArea: PlantingSiteAreaDto;

    set collection(value: PhotoCollectionDto) {
        if (value && this._collection !== value) {
            this.siteArea.collections = [value, ...this.siteArea.collections.filter(c => c.id != value.id)];
        }
        this._collection = value;
        if (value && value.photos) {
            this.photos = this.sortPhotos(value.photos);
        } else {
            this.photos = [];
        }
    }

    private oldLat: number;
    private oldLng: number;

    _collection: PhotoCollectionDto;

    photos: PhotoDto[] = [];

    files?: File[];

    editPhoto?: PhotoDto;

    fullscreenPhoto: PhotoDto;

    currentUser: AppUserModel;

    constructor(
        private collectionService: PhotoCollectionService, 
        private canDeactivateService: CanDeactivateService,
        private route: ActivatedRoute,
        private userService: UserService) {
        super();
    }

    ngOnInit() {
        this.getCurrentUser();
        this.route.paramMap.subscribe(params => {
            this.collection = this.siteArea.collections.find(c => c.id == this.collectionId);
        });
        this.wizardModal.onHidden.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.files = undefined);
        this.editModal.onHidden.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.editPhoto = undefined);
    }

    showFullscreen(photo: PhotoDto) {
        this.fullscreenPhoto = photo;
        this.fullscreenModal.show();
    }

    validFileMimeTypes = ['image/jpeg', 'image/png'];

    filesSelected(fileList: FileList) {
        let files: File[] = [];
        for (let i = 0; i < fileList.length; i++) {
            const file = fileList[i];
            if (this.validFileMimeTypes.includes(file.type))
                files.push(file);
        }
        if (files.length == 0) return;
        this.files = files;
        this.wizardModal.show();
    }

    closeWizard() {
        this.fileInput.nativeElement.value = '';
        this.wizardModal.hide();
    }

    async closeEditModal(){
        let shouldShowConfirmLeaving = this.shouldShowConfirmLeaving();
        if (shouldShowConfirmLeaving){
            let leaveThePage = await this.canDeactivateService.showWarning();
            if (!leaveThePage)
                return;
        }
        this.editModal.hide()
    }

    private shouldShowConfirmLeaving(){
        let isLatChange = this.oldLat != +this.editPhoto.lat;
        let isLngChange = this.oldLng != +this.editPhoto.lon;
        return isLatChange || isLngChange;
    }

    photoChanged(photo: PhotoDto) {
        this._collection.photos = this.sortPhotos([{...photo}, ...this.photos.filter(p => p.id != photo.id)]);
        this.photos = this._collection.photos;
    }

    sortPhotos(photos: PhotoDto[]) {
        return ArrayHelper.sortBy(photos, false, p => new Date(p.lastUpdated));
    }

    showEditValidation = false;
    showEdit(photo: PhotoDto) {
        this.editPhoto = {...photo};
        this.editModal.show();

        this.oldLat = this.editPhoto.lat;
        this.oldLng = this.editPhoto.lon;
    }

    async saveEdit() {
        if (!this.editPhoto.lat || !this.editPhoto.lon) {
            this.showEditValidation = true;
            return;
        }
        this.editModal.hide();
        let photo = this.editPhoto;
        this.showEditValidation = false;
        await this.collectionService.updatePhoto(photo);
        photo.lastUpdated = new Date();
        this.photoChanged(photo);
    }

    async setThumbnail(photo: PhotoDto) {
        await this.collectionService.setCollectionThumbnail(this._collection.id, photo.id);
        this.collection = {...this._collection, thumbnailPhotoId: photo.id};
    }

    async deletePhoto(photo: PhotoDto) {
        let deleted = await this.confirmDelete.showAwait(photo.id);
        if (!deleted) return;
        await this.collectionService.deletePhoto(photo.id, this._collection.plantingSiteId);
        this._collection.photos = [...this.photos.filter(p => p.id != photo.id)];
        this.photos = this._collection.photos;
    }

    async getCurrentUser() {
        this.currentUser = await this.userService.getCurrentUserAsync();
    }
}
